import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check, Sparkles } from 'lucide-react';

/**
 * WelcomeLanguageModal
 *
 * A delightful, one-time modal shown after the user accepts the privacy
 * consent on first launch. It celebrates Falak's 13-language reach and
 * lets the user confirm or switch language before entering the app.
 *
 * Storage keys:
 *   - Trigger:   `falak.privacy_consent.v1`   (must exist)
 *   - Dismiss:   `falak.welcome_shown.v1`     (blocks re-appearance)
 *
 * Design: 2-column grid of 13 flag chips with a soft amber glow on the
 * currently active one. Tapping any flag switches i18next language
 * instantly (preview) — user then taps "Continue" to lock it in.
 */

const CONSENT_KEY = 'falak.privacy_consent.v1';
const SHOWN_KEY   = 'falak.welcome_shown.v1';

const LANGS = [
  // Interleaved order — no geographic clustering. Every row mixes scripts /
  // regions so no continent feels privileged. Arabic sits at the very bottom
  // as a quiet signature (spotlight row 7).
  { code: 'en', label: 'English',          flag: '🇬🇧' },  // row 1 · Latin
  { code: 'ur', label: 'اردو',              flag: '🇵🇰' },  // row 1 · Arabic script
  { code: 'zh', label: '中文',              flag: '🇨🇳' },  // row 2 · Chinese
  { code: 'fr', label: 'Français',         flag: '🇫🇷' },  // row 2 · Latin
  { code: 'bn', label: 'বাংলা',              flag: '🇧🇩' },  // row 3 · Bengali
  { code: 'de', label: 'Deutsch',          flag: '🇩🇪' },  // row 3 · Latin
  { code: 'fa', label: 'فارسی',            flag: '🇮🇷' },  // row 4 · Persian
  { code: 'es', label: 'Español',          flag: '🇪🇸' },  // row 4 · Latin
  { code: 'id', label: 'Bahasa Indonesia', flag: '🇮🇩' },  // row 5 · Asian Latin
  { code: 'tr', label: 'Türkçe',           flag: '🇹🇷' },  // row 5 · Turkic
  { code: 'pt', label: 'Português',        flag: '🇧🇷' },  // row 6 · Latin
  { code: 'it', label: 'Italiano',         flag: '🇮🇹' },  // row 6 · Latin
  { code: 'ar', label: 'العربية',           flag: '🇸🇦' },  // row 7 · Arabic (spotlight)
];

const RTL = new Set(['ar', 'ur', 'fa']);

// Localized strings for every supported language.
const L = {
  en: {
    title: 'Falak speaks 13 languages',
    subtitle: 'Pick the one closest to your heart — you can change it anytime.',
    cta: 'Continue',
    footer: 'Change the language anytime from the top bar',
  },
  ar: {
    title: 'فَلَك يتحدّث ١٣ لغة',
    subtitle: 'اختر الأقرب إلى قلبك — يمكنك تغييرها متى شئت.',
    cta: 'متابعة',
    footer: 'يمكنك تغيير اللغة لاحقاً من الشريط العلوي',
  },
  fr: {
    title: 'Falak parle 13 langues',
    subtitle: 'Choisissez celle qui vous parle le plus — modifiable à tout moment.',
    cta: 'Continuer',
    footer: 'Changez de langue à tout moment depuis la barre du haut',
  },
  de: {
    title: 'Falak spricht 13 Sprachen',
    subtitle: 'Wähle die, die deinem Herzen am nächsten ist — jederzeit änderbar.',
    cta: 'Weiter',
    footer: 'Sprache jederzeit über die obere Leiste ändern',
  },
  it: {
    title: 'Falak parla 13 lingue',
    subtitle: 'Scegli quella più vicina al tuo cuore — modificabile in ogni momento.',
    cta: 'Continua',
    footer: 'Cambia lingua in qualsiasi momento dalla barra superiore',
  },
  es: {
    title: 'Falak habla 13 idiomas',
    subtitle: 'Elige el que más te acerque — puedes cambiarlo cuando quieras.',
    cta: 'Continuar',
    footer: 'Cambia el idioma cuando quieras desde la barra superior',
  },
  pt: {
    title: 'Falak fala 13 idiomas',
    subtitle: 'Escolha o mais próximo do seu coração — pode mudar quando quiser.',
    cta: 'Continuar',
    footer: 'Altere o idioma a qualquer momento na barra superior',
  },
  tr: {
    title: 'Falak 13 dil konuşur',
    subtitle: 'Kalbinize en yakın olanı seçin — istediğiniz zaman değiştirebilirsiniz.',
    cta: 'Devam et',
    footer: 'Dili istediğiniz zaman üst çubuktan değiştirin',
  },
  ur: {
    title: 'فلک ۱۳ زبانیں بولتا ہے',
    subtitle: 'اپنے دل کے قریب ترین منتخب کریں — جب چاہیں تبدیل کریں۔',
    cta: 'جاری رکھیں',
    footer: 'اوپر کی پٹی سے کسی بھی وقت زبان تبدیل کریں',
  },
  fa: {
    title: 'فلک به ۱۳ زبان سخن می‌گوید',
    subtitle: 'زبان نزدیک به قلب خود را انتخاب کنید — هر زمان قابل تغییر است.',
    cta: 'ادامه',
    footer: 'زبان را هر زمان از نوار بالا تغییر دهید',
  },
  id: {
    title: 'Falak berbicara 13 bahasa',
    subtitle: 'Pilih yang paling dekat dengan hatimu — bisa diubah kapan saja.',
    cta: 'Lanjut',
    footer: 'Ubah bahasa kapan saja dari bilah atas',
  },
  bn: {
    title: 'ফালাক ১৩টি ভাষায় কথা বলে',
    subtitle: 'আপনার হৃদয়ের সবচেয়ে কাছের ভাষাটি বেছে নিন — যে কোনো সময় পরিবর্তন করা যায়।',
    cta: 'চালিয়ে যান',
    footer: 'উপরের বার থেকে যে কোনো সময় ভাষা পরিবর্তন করুন',
  },
  zh: {
    title: 'Falak 支持 13 种语言',
    subtitle: '选择最贴近您心灵的语言——随时可以更改。',
    cta: '继续',
    footer: '随时可从顶部栏更改语言',
  },
};

const WelcomeLanguageModal = () => {
  const { i18n } = useTranslation();
  const [visible, setVisible] = useState(false);

  // Wait until privacy consent exists, then show once.
  useEffect(() => {
    const check = () => {
      try {
        const consent = localStorage.getItem(CONSENT_KEY);
        const shown   = localStorage.getItem(SHOWN_KEY);
        if (consent && !shown) {
          // Slight delay so it doesn't chain-pop right after privacy dismissal
          setTimeout(() => setVisible(true), 350);
          return true;
        }
      } catch { /* localStorage blocked — silently skip */ }
      return false;
    };

    if (check()) return;

    // Poll gently while user is on the privacy modal (max ~2 min)
    const interval = setInterval(() => {
      if (check()) clearInterval(interval);
    }, 500);
    const stop = setTimeout(() => clearInterval(interval), 120000);
    return () => { clearInterval(interval); clearTimeout(stop); };
  }, []);

  const current = i18n.language || 'en';
  const strings = L[current] || L.en;
  const isRTL   = RTL.has(current);

  const pickLang = (code) => {
    i18n.changeLanguage(code);
  };

  const confirm = () => {
    try {
      localStorage.setItem(SHOWN_KEY, JSON.stringify({
        chosen: i18n.language,
        timestamp: new Date().toISOString(),
        version: 'v1',
      }));
    } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center p-4"
      style={{
        background: 'rgba(0, 0, 0, 0.72)',
        backdropFilter: 'blur(12px)',
        animation: 'falak-welcome-fade-in 0.3s ease-out',
      }}
      data-testid="welcome-language-modal"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <style>{`
        @keyframes falak-welcome-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes falak-welcome-slide-up {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes falak-welcome-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(245, 158, 11, 0.35); }
          50%      { box-shadow: 0 0 32px rgba(245, 158, 11, 0.55); }
        }
      `}</style>

      <div
        className="w-full max-w-md rounded-2xl border overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, #14213D 0%, #0B132B 100%)',
          borderColor: 'rgba(245, 158, 11, 0.28)',
          boxShadow: '0 24px 70px rgba(0, 0, 0, 0.55), 0 0 90px rgba(245, 158, 11, 0.12)',
          animation: 'falak-welcome-slide-up 0.45s ease-out',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header — Globe + sparkles + title */}
        <div
          className="px-6 py-5 border-b flex-shrink-0"
          style={{ borderColor: 'rgba(255, 255, 255, 0.06)' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="flex items-center justify-center rounded-full relative"
              style={{
                width: 46,
                height: 46,
                background: 'rgba(245, 158, 11, 0.14)',
                border: '1px solid rgba(245, 158, 11, 0.4)',
                animation: 'falak-welcome-glow 3s ease-in-out infinite',
              }}
            >
              <Globe size={22} color="#F59E0B" />
              <Sparkles
                size={10}
                color="#FCD34D"
                style={{
                  position: 'absolute',
                  top: -2,
                  right: -2,
                  filter: 'drop-shadow(0 0 4px #FCD34D)',
                }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <h2
                className="text-lg font-semibold leading-tight mb-1"
                style={{ color: '#FFFFFF' }}
                data-testid="welcome-language-title"
              >
                {strings.title}
              </h2>
              <p
                className="text-[12px] leading-snug"
                style={{ color: '#94A3B8' }}
              >
                {strings.subtitle}
              </p>
            </div>
          </div>
        </div>

        {/* Language grid — scrollable */}
        <div
          className="px-4 py-3 overflow-y-auto"
          style={{ flex: '1 1 auto' }}
          data-testid="welcome-language-grid"
        >
          <div className="grid grid-cols-2 gap-2">
            {LANGS.map((lang) => {
              const active = lang.code === current;
              return (
                <button
                  key={lang.code}
                  onClick={() => pickLang(lang.code)}
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition-all hover:-translate-y-0.5"
                  style={{
                    background: active ? 'rgba(245, 158, 11, 0.16)' : 'rgba(255,255,255,0.03)',
                    border: '1px solid ' + (active ? 'rgba(245, 158, 11, 0.5)' : 'rgba(255,255,255,0.06)'),
                  }}
                  data-testid={`welcome-lang-${lang.code}`}
                  dir="ltr"
                >
                  <span className="text-lg flex-shrink-0">{lang.flag}</span>
                  <span
                    className="text-[13px] font-medium truncate flex-1 text-left"
                    style={{ color: active ? '#FCD34D' : '#E2E8F0' }}
                  >
                    {lang.label}
                  </span>
                  {active && (
                    <Check
                      size={14}
                      style={{ color: '#F59E0B', flexShrink: 0 }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer — Continue */}
        <div
          className="px-6 py-4 flex-shrink-0"
          style={{
            background: 'rgba(0, 0, 0, 0.25)',
            borderTop: '1px solid rgba(255, 255, 255, 0.06)',
          }}
        >
          <button
            onClick={confirm}
            className="w-full py-3 rounded-xl text-sm font-semibold transition-all hover:brightness-110 active:scale-[0.98]"
            style={{
              background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
              color: '#0B132B',
              boxShadow: '0 4px 18px rgba(245, 158, 11, 0.4)',
            }}
            data-testid="welcome-language-continue"
          >
            {strings.cta}
          </button>

          <p
            className="text-[10px] text-center mt-3 leading-relaxed"
            style={{ color: 'rgba(255, 255, 255, 0.4)' }}
          >
            {strings.footer}
          </p>
        </div>
      </div>
    </div>
  );
};

export default WelcomeLanguageModal;
